import React from 'react';
import {toast} from 'react-toastify';
import {useGetWorkspaceProductsQuery} from '../../store/Api';
import {usePutWorkspaceItemDetailsMutation} from '../../store/endpoints/WorkspaceItemsEndpoints';
import {IWorkspaceItem, IWorkspaceItemDetails} from '../../models/Workspace';

/**
 * Bulk edit for the workspace grid's column-header controls: apply one details change to every
 * eligible line at once (taxable, tax code, source). Each line saves through the same per-item PUT
 * the cell uses, so what lands is exactly what a hand edit would have written, and one persistent
 * toast reports progress and the outcome.
 */
const APPLY_CHUNK = 10;

export const useApplyToWorkspaceItems = (workspaceId: string) => {
    const products = useGetWorkspaceProductsQuery(workspaceId, {skip: !workspaceId || workspaceId.trim().length === 0});
    const [putDetails] = usePutWorkspaceItemDetailsMutation();
    const [applying, setApplying] = React.useState(false);

    const apply = React.useCallback(async (
        label: string,
        eligible: (item: IWorkspaceItem) => boolean,
        change: (item: IWorkspaceItem) => Partial<IWorkspaceItemDetails>,
    ) => {
        const targets = (products.data ?? []).filter((item) => !!item.id && eligible(item));
        if (targets.length === 0) {
            toast.info(`No lines to update for ${label}`, {autoClose: 2500});
            return;
        }
        setApplying(true);
        const toastId = toast.loading(`Setting ${label} on ${targets.length} line${targets.length === 1 ? '' : 's'}…`);
        // In chunks: a large workspace would otherwise open one request per line at once, which the browser
        // queues anyway and the API sees as a burst.
        const failedPerChunk: number[] = [];
        for (let i = 0; i < targets.length; i += APPLY_CHUNK) {
            const results = await Promise.allSettled(targets.slice(i, i + APPLY_CHUNK).map((item) =>
                putDetails({workspace: workspaceId, product: item.id, details: {...item.details, ...change(item)}}).unwrap()));
            failedPerChunk.push(results.filter((r) => r.status === 'rejected').length);
        }
        const failed = failedPerChunk.reduce((a, b) => a + b, 0);
        setApplying(false);
        if (failed === 0) {
            toast.update(toastId, {type: 'success', render: `${label} set on ${targets.length} line${targets.length === 1 ? '' : 's'}`, isLoading: false, autoClose: 3000});
        } else {
            toast.update(toastId, {type: 'error', render: `${label}: ${failed} of ${targets.length} line${targets.length === 1 ? '' : 's'} failed to save`, isLoading: false, autoClose: 5000});
        }
    }, [products.data, putDetails, workspaceId]);

    return {apply, applying, items: products.data ?? []};
};
